// apps/client/src/utils/edm.ts
//
// Pure helpers and copy for the eDM Campaigns surface (/leads/edm). Kept free
// of React and Firebase so every predicate the nav, the route guards and the
// pages share is unit-testable on its own.
//
// The callables (edmCampaignAction / edmImportAction) are the authority for
// every rule here. Nothing in this file widens what the server allows; the
// predicates mirror it so the UI never offers an action the server refuses.

import { BUSINESS_TZ } from './businessTime';

// ---------------------------------------------------------------- gating

/**
 * The single eDM entitlement gate. Strictly `=== true`: a string 'true', 1 or
 * an object never enables it, and absent means off. Used by the top nav and
 * by RequireEdmEnabled so the two cannot disagree.
 */
export const isEdmEnabled = (f?: Record<string, unknown> | null): boolean =>
  f?.edm_broadcast === true;

/**
 * Phase 1d / 2 / 4 flags (plan-v3 core §2): each strictly `=== true` AND
 * requires `edm_broadcast === true`, mirroring the server helpers in
 * services/client-functions/src/edm/edmFlag.ts. Absent means off.
 */
export const isEdmContentEnabled = (f?: Record<string, unknown> | null): boolean =>
  isEdmEnabled(f) && f?.edm_content === true;

export const isEdmTrackingEnabled = (f?: Record<string, unknown> | null): boolean =>
  isEdmEnabled(f) && f?.edm_tracking === true;

export const isEdmPreferencesEnabled = (f?: Record<string, unknown> | null): boolean =>
  isEdmEnabled(f) && f?.edm_preferences === true;

/** edmCampaignAction enforceRole(['super_admin','administrator','marketing']). */
export const EDM_ROLES = ['super_admin', 'administrator', 'marketing'] as const;

/**
 * Who may import contacts: the company-wide subset of EDM_ROLES. Mirrors the
 * import callable and the storage rule, so an office-scoped administrator
 * never uploads a file the server would then refuse to read. office_admin is
 * not an eDM role at all, so canSeeAllOffices does not admit it.
 */
export function canImportEdmContacts(role?: string, canSeeAllOffices?: boolean): boolean {
  if (role === 'super_admin' || role === 'marketing') return true;
  return role === 'administrator' && canSeeAllOffices === true;
}

/** Marketing never emails clients (`clients_not_available_for_role`). */
export const canUseClientsSource = (role?: string): boolean =>
  role === 'super_admin' || role === 'administrator';

// ---------------------------------------------------------------- merge fields

/** Merge fields the composer offers and the server can fill. */
export const EDM_MERGE_FIELDS = ['firstName', 'lastName', 'companyName', 'brokerName'] as const;

/** Merge fields offered in the subject line. */
export const EDM_SUBJECT_MERGE_FIELDS = ['firstName', 'companyName'] as const;

/**
 * Single-brace tokens such as `{firstName}`. The re-engagement editor uses
 * that syntax, but eDM sends it literally, so the composer blocks it. The
 * leading group stops `{{x}}` matching on its inner brace; the lookahead
 * stops `{x}}` matching.
 */
export function findSingleBraceTokens(text: string): string[] {
  const out: string[] = [];
  const re = /(^|[^{])\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}(?!\})/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) out.push(m[2]);
  return out;
}

/** Double-brace tokens that are not in EDM_MERGE_FIELDS. */
export function findUnknownMergeTokens(text: string): string[] {
  const out: string[] = [];
  const allowed = new Set<string>(EDM_MERGE_FIELDS);
  const re = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (!allowed.has(m[1])) out.push(m[1]);
  }
  return out;
}

export const SINGLE_BRACE_MESSAGE =
  'Use double braces: {{firstName}}. Single braces are sent as written.';

export const unknownMergeTokenMessage = (token: string): string =>
  `{{${token}}} is not a field we can fill. Allowed: ${EDM_MERGE_FIELDS.join(', ')}.`;

/** First lint error for one field's text, or null when it is clean. */
export function mergeLintError(text: string): string | null {
  if (findSingleBraceTokens(text).length > 0) return SINGLE_BRACE_MESSAGE;
  const unknown = findUnknownMergeTokens(text);
  if (unknown.length > 0) return unknownMergeTokenMessage(unknown[0]);
  return null;
}

// ---------------------------------------------------------------- status

export type EdmCampaignStatus =
  | 'draft'
  | 'freezing'
  | 'frozen'
  | 'scheduled'
  | 'sending'
  | 'completed'
  | 'paused'
  | 'cancelled'
  | 'ab_waiting';

export const STATUS_LABEL: Record<EdmCampaignStatus, string> = {
  draft: 'Draft',
  freezing: 'Freezing list…',
  frozen: 'List frozen',
  scheduled: 'Scheduled',
  sending: 'Sending',
  completed: 'Completed',
  paused: 'Paused',
  cancelled: 'Cancelled',
  ab_waiting: 'A/B test: picking the winner',
};

export const STATUS_CHIP_CLASS: Record<EdmCampaignStatus, string> = {
  draft: 'bg-gray-700/40 text-gray-300 border-gray-600',
  freezing: 'bg-blue-500/10 text-blue-300 border-blue-500/30',
  frozen: 'bg-blue-500/10 text-blue-300 border-blue-500/30',
  scheduled: 'bg-blue-500/15 text-blue-200 border-blue-500/40',
  sending: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  completed: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  paused: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  cancelled: 'bg-gray-800 text-gray-400 border-gray-700',
  ab_waiting: 'bg-blue-500/15 text-blue-200 border-blue-500/40',
};

export const statusLabel = (status: string): string =>
  STATUS_LABEL[status as EdmCampaignStatus] ?? status;

export const statusChipClass = (status: string): string =>
  STATUS_CHIP_CLASS[status as EdmCampaignStatus] ?? STATUS_CHIP_CLASS.draft;

// ---------------------------------------------------------------- paused reasons

/** Who may press Resume for a paused reason. */
export type ResumePolicy = 'hidden' | 'enabled' | 'super_admin_only';

export interface PausedReasonCopy {
  body: string;
  resume: ResumePolicy;
  /** Optional fix-it link shown under the body. */
  link?: { to: string; label: string; superAdminOnly?: boolean };
}

const NOTIFIED = 'VALUTTEN has been notified.';

/**
 * Body copy per `pausedReason`. "VALUTTEN has been notified" appears on every
 * reason that posts a Chat card, which is every reason except `flag_off`.
 */
export const PAUSED_REASON_COPY: Record<string, PausedReasonCopy> = {
  flag_off: {
    body: 'eDM has been switched off for your company, so sending stopped. Contact VALUTTEN support to turn it back on.',
    resume: 'hidden',
  },
  subscription_inactive: {
    body: `Your VALUTTEN subscription is not active, so sending stopped. Update billing, then resume. ${NOTIFIED}`,
    resume: 'enabled',
    link: { to: '/settings/billing', label: 'Update billing', superAdminOnly: true },
  },
  sender_identity_incomplete: {
    body: `Your sender details are incomplete (legal name, ABN, address or reply-to email). Fix them in Setup Wizard › Branding, then resume. ${NOTIFIED}`,
    resume: 'enabled',
    link: { to: '/leads/setup', label: 'Open Setup Wizard' },
  },
  delivery_feedback_not_configured: {
    body: `VALUTTEN's delivery tracking is not yet active for eDM. Nothing more is sent until it is. ${NOTIFIED}`,
    resume: 'enabled',
  },
  provider_rejected: {
    body: `Our email provider refused to send for this campaign ({name}). ${NOTIFIED} VALUTTEN will contact you.`,
    resume: 'enabled',
  },
  provider_unavailable: {
    body: `Our email provider stopped confirming sends, so we paused to avoid duplicates. ${NOTIFIED}`,
    resume: 'enabled',
  },
  send_failures: {
    body: `Five emails in a row failed, so we paused to protect your sender reputation. ${NOTIFIED}`,
    resume: 'enabled',
  },
  daily_quota_exceeded: {
    body: `VALUTTEN's daily sending limit was reached. Resume tomorrow to send the rest. ${NOTIFIED}`,
    resume: 'enabled',
  },
  quota_exhausted: {
    body: `VALUTTEN's sending limit with our email provider was reached, so sending stopped. ${NOTIFIED} Resume once VALUTTEN confirms the limit has reset.`,
    resume: 'enabled',
  },
  bounce_rate: {
    body: `Too many emails bounced or were marked as spam (2% bounces or 0.05% complaints), so we paused to protect your sender reputation. Check the list was collected with consent before resuming. ${NOTIFIED}`,
    resume: 'super_admin_only',
  },
  team_shape_invalid: {
    body: `VALUTTEN's eDM sending account is not set up as expected, so we paused before sending anything more. ${NOTIFIED}`,
    resume: 'enabled',
  },
  delivery_feedback_stale: {
    body: `We stopped receiving delivery results from receiving mail servers, so we paused rather than send without them. ${NOTIFIED}`,
    resume: 'enabled',
  },
  no_progress: {
    body: `Sending stopped making progress, so we paused to avoid duplicate emails. ${NOTIFIED}`,
    resume: 'enabled',
  },
  scheduler_revoked: {
    body: `The person who scheduled this campaign no longer has a role that can send it, so sending stopped. ${NOTIFIED}`,
    resume: 'enabled',
  },
  // Resume cannot fix these: the sender snapshot is frozen and the Send as
  // select is locked once the campaign leaves draft, so Resume is hidden.
  sender_broker_inactive: {
    body: 'The broker this campaign sends as is no longer active. Cancel this campaign and create a new one with another sender.',
    resume: 'hidden',
  },
  sender_broker_changed: {
    body: "The broker's email address changed after this campaign was scheduled. Cancel this campaign and create a new one to send with the new Reply-To.",
    resume: 'hidden',
  },
  sending_key_domain_restricted: {
    body: 'Sending from your domain is not enabled yet. VALUTTEN support has been notified.',
    resume: 'enabled',
  },
  payload_invalid: {
    body: `Our email provider would not accept this campaign's content, so sending stopped. ${NOTIFIED} VALUTTEN will contact you.`,
    resume: 'enabled',
  },
};

/**
 * Copy for any `pausedReason`, including `provider_rejected:{name}` and
 * values this build does not know (rendered with the raw reason).
 */
export function pausedReasonCopy(reason: string | null | undefined): PausedReasonCopy {
  const raw = reason ?? '';
  const colon = raw.indexOf(':');
  const key = colon >= 0 ? raw.slice(0, colon) : raw;
  const detail = colon >= 0 ? raw.slice(colon + 1) : '';
  const known = PAUSED_REASON_COPY[key];
  if (known) {
    return { ...known, body: known.body.replace('{name}', detail || 'no detail given') };
  }
  return {
    body: `Sending is paused (${raw || 'no reason given'}). Contact VALUTTEN support.`,
    resume: 'enabled',
  };
}

/** Whether `role` sees the Resume button for this reason. */
export function canResumeForReason(reason: string | null | undefined, role?: string): boolean {
  const policy = pausedReasonCopy(reason).resume;
  if (policy === 'hidden') return false;
  if (policy === 'super_admin_only') return role === 'super_admin';
  return true;
}

// ---------------------------------------------------------------- errors

interface CallableErrorLike {
  code?: unknown;
  message?: unknown;
  details?: unknown;
}

/** The machine reason of a callable error (`details.code`), or ''. */
export function edmErrorReason(err: unknown): string {
  const details = (err as CallableErrorLike | null)?.details;
  if (details && typeof details === 'object') {
    const code = (details as Record<string, unknown>).code;
    if (typeof code === 'string') return code;
  }
  return '';
}

/** `details.limit` formatted for copy, or the documented default. */
function limitOr(details: Record<string, unknown>, fallback: string): string {
  const limit = details.limit;
  return typeof limit === 'number' && Number.isFinite(limit) ? limit.toLocaleString('en-AU') : fallback;
}

/** On-screen names of the parts `content_incomplete` can list in `details.missing`. */
const CONTENT_PART_LABELS: Record<string, string> = {
  subject: 'Subject line',
  heading: 'Heading',
  intro: 'Intro',
  body: 'Body',
  signOff: 'Sign-off',
  content: 'email content',
};

const HTML_UNSAFE_COPY = 'Your HTML could not be made safe to send. Remove scripts, forms and unusual characters, then try again.';
const CONTENT_REJECTED_FALLBACK = 'This email cannot be used as it is. Check the subject line and content, then try again.';

/** "A", "A and B", "A, B and C". */
function joinAnd(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * `content_rejected` covers several causes, told apart by `details.detail`
 * (services/client-functions/src/edm/edmContent.ts, edmBlocks.ts,
 * edmCampaignAction.ts). Known details get their own copy; for any other
 * detail the server's message is shown (it is written as user copy), and
 * only when there is none, a neutral fallback. The HTML-safety copy is
 * reserved for the sanitiser's own details.
 */
function contentRejectedMessage(details: Record<string, unknown>, serverMessage: unknown): string {
  const detail = typeof details.detail === 'string' ? details.detail : '';
  switch (detail) {
    case 'content_incomplete': {
      const missing = Array.isArray(details.missing)
        ? details.missing.map((m) => (typeof m === 'string' ? CONTENT_PART_LABELS[m] : undefined)).filter((m): m is string => !!m)
        : [];
      if (missing.length === 0) return 'Add a subject line and the email content first.';
      return `Fill in the ${joinAnd(missing)} first.`;
    }
    case 'subject':
      return 'The subject line is too long or contains line breaks or other control characters. Keep it to 200 characters on one line.';
    case 'subject_b_missing':
      return 'Add a second subject line for the A/B test.';
    case 'cta_url_missing':
      return 'The button needs a link. Add a Button link, or clear the Button label.';
    case 'cta_url_invalid':
      return 'The Button link must be a plain https:// address.';
    case 'brace_entity':
    case 'not_fixed_point':
      return HTML_UNSAFE_COPY;
    case 'malformed_merge_field':
      return 'A merge field is not written correctly. Use double braces around a field name, for example {{firstName}}.';
    case 'attribute_merge_token':
      return 'Merge fields cannot be used inside HTML tags or links. The only exception is {{unsubscribeUrl}} as a link address.';
    default:
      break;
  }
  const message = typeof serverMessage === 'string' ? serverMessage.trim() : '';
  // A sentence, not a bare status word such as "internal".
  return /\s/.test(message) ? message : CONTENT_REJECTED_FALLBACK;
}

/**
 * Whether a `content_rejected` refusal is about the custom HTML or block
 * content (shown beside that editor), rather than the subject line, the
 * fixed-layout fields or missing content (shown as a page-level error).
 */
export function edmContentRejectionIsAboutHtml(err: unknown): boolean {
  if (edmErrorReason(err) !== 'content_rejected') return false;
  const details = (err as CallableErrorLike | null)?.details as Record<string, unknown> | undefined;
  const detail = typeof details?.detail === 'string' ? details.detail : '';
  return !['content_incomplete', 'subject', 'subject_b_missing', 'cta_url_missing', 'cta_url_invalid'].includes(detail);
}

/**
 * Callable failure to a sentence the user can act on. Reads
 * `details.code` first (the eDM error contract), then the
 * `functions/<code>` prefix. Never switches on the server message, so a
 * server rewording cannot silently change what the user sees. The one
 * place the server message is displayed is a `content_rejected` refusal
 * whose `details.detail` this build does not know (see
 * contentRejectedMessage).
 */
export function edmErrorMessage(err: unknown): string {
  const e = (err ?? {}) as CallableErrorLike;
  const details = (e.details && typeof e.details === 'object' ? e.details : {}) as Record<string, unknown>;
  const reason = edmErrorReason(err);
  const code = typeof e.code === 'string' ? e.code.replace(/^functions\//, '') : '';
  const field = typeof details.field === 'string' ? details.field : 'field';
  const day = typeof details.day === 'string' ? details.day : 'that day';

  switch (reason) {
    case 'edm_disabled':
      return 'eDM is not switched on for your company.';
    case 'clients_not_available_for_role':
      return 'Your role cannot email clients. Choose leads or imported contacts.';
    case 'delivery_feedback_not_configured':
      return "VALUTTEN's delivery tracking for eDM is not active yet, so campaigns cannot be scheduled. VALUTTEN has been notified.";
    case 'sender_identity_incomplete':
      return 'Your sender details are incomplete. Fix them in Setup Wizard › Branding.';
    case 'sender_identity_unverified':
      return 'Your sender details could not be confirmed: the company name cannot name VALUTTEN or another business, and the ABN must match the one VALUTTEN holds for you. Fix them in Setup Wizard › Branding or contact VALUTTEN support.';
    case 'schedule_in_past':
      return 'Pick a time in the future.';
    case 'schedule_day_taken':
      return `Another campaign already starts on ${day} (Brisbane). Pick a different day.`;
    case 'too_many_active':
      return 'You already have 3 active campaigns. Wait for one to finish or cancel one.';
    case 'test_send_limit':
      return "You've sent 5 tests for this campaign today (Brisbane time). Try again tomorrow.";
    case 'content_rejected':
      return contentRejectedMessage(details, e.message);
    case 'content_too_large':
      return 'This email is too large. Shorten it (under 200 KB of HTML).';
    case 'single_brace_merge_token':
      return 'Use double braces for merge fields, for example {{firstName}}.';
    case 'blocked_unresolved_merge_field':
      return `{{${field}}} has nothing to fill it. Remove it or use an allowed field.`;
    case 'merge_field_unavailable_for_source':
      return `{{${field}}} cannot be filled for one of the audiences you chose. Remove it or change the audience.`;
    case 'edm_monthly_limit':
      return 'This campaign would take you over your monthly eDM limit. Reduce the audience or wait until next month (Brisbane time).';
    case 'first_send_cap':
      return 'The first campaign to a newly imported list can go to at most 500 recipients. Send a smaller campaign first.';
    case 'not_an_xlsx':
      return "This isn't a valid Excel (.xlsx) file. Re-save it from Excel as .xlsx, or export as CSV.";
    case 'too_many_rows':
      return 'This file has more than 20,000 rows. Split it into smaller files.';
    case 'multiple_sheets':
      return 'This Excel file has more than one sheet with data. Keep only the contacts sheet, or export it as CSV.';
    case 'import_params_mismatch':
      return 'This import was started with different settings. Start a new import.';
    case 'attestation_mismatch':
      return 'The confirmation text is out of date. Reload the page and try again.';
    case 'attestation_required':
      return 'Tick the confirmation box to import contacts.';
    case 'contacts_not_available_for_role':
      return 'Your role cannot use imported contacts.';
    case 'import_not_available_for_role':
      return 'Your role cannot import contacts.';
    case 'invalid_state':
      return "This action is not available in the campaign's current state. Refresh and try again.";
    case 'audience_too_large':
      return 'Your audience is over 20,000 recipients. Narrow the sources.';
    case 'test_send_failed':
      return 'The test email could not be sent. Try again in a minute.';
    case 'too_many_pages':
      return 'This PDF has more than 200 pages. Split it.';
    case 'requires_negative_role':
      return 'This file can only be imported as an unsubscribe or bounce list.';
    case 'import_in_progress':
      return 'This import is still running. Come back in a few minutes.';
    case 'schedule_beyond_test_window':
      return 'Schedule within 7 days of your last delivered test send, or send a new test.';
    case 'test_send_stale':
      // Fix pass X17: the content-changed variant (only with a v3 flag on);
      // the 1c copy is unchanged for every other case.
      if (details.reason === 'content_changed') return 'You changed this email after your last test. Send a new test and wait for it to be delivered.';
      return 'Send a new test before resuming; the last delivered test is older than 7 days.';
    case 'first_send_cap_in_use':
      return 'Another campaign is already using this newly imported list. Wait for it to finish.';
    case 'sender_broker_invalid':
      return 'This campaign cannot be sent as that team member. Choose an active broker in your offices, or send as your company.';
    case 'test_send_domain_mismatch':
      return 'Your last test was sent from a different domain. Send a new test before scheduling.';
    case 'tenant_domain_unverified':
      return 'Your sending domain is not verified yet. Verify it on the Sending domain page before you freeze the list.';
    // Plan-v3 core refusal codes (returned only when edm_content /
    // edm_tracking / edm_preferences is on).
    case 'tag_not_registered':
      return 'That tag is not in your tag list. Add it under Tags first, then try again.';
    case 'too_many_links':
      return 'This email has more than 50 links, so they cannot be tracked. Remove some links, then try again.';
    case 'ab_requires_tracking':
      return 'An A/B subject test needs open and click tracking switched on for your company.';
    case 'topic_required':
      return 'Choose the topic this campaign is about, so recipients can manage what they receive.';
    case 'resend_exists':
      return 'This campaign already has a resend to non-openers. Open that one instead.';
    // Plan-v3 core codes added by slices A, B and D (wired in slice F).
    case 'tag_filter_too_broad':
      return `More than ${limitOr(details, '5,000')} records carry these tags, so they cannot be used as a filter. Choose a narrower tag.`;
    case 'too_many_tags':
      return `A record can carry at most ${limitOr(details, '20')} tags. Remove one, then try again.`;
    case 'clients_not_available':
      return 'Your role cannot tag clients.';
    case 'count_job_expired':
      return 'This audience count has expired. Start the count again.';
    case 'count_job_stale':
      return 'The audience changed while it was being counted. Start the count again.';
    case 'count_too_large':
      return 'This audience is too large to count here. Narrow the sources or tags.';
    case 'topic_in_use':
      return 'A campaign that has not finished uses this topic. Cancel or finish that campaign first, then remove the topic.';
    case 'topics_company_wide_only':
      return 'Topics can be changed only by a Super Admin, Marketing, or a team member who can see every office.';
    case 'resend_requires_tracking':
      return 'Resending to non-openers needs open tracking: it must be switched on for your company, and the original campaign must have been sent with it.';
    case 'resend_window':
      return 'A resend to non-openers can be created from 24 hours to 30 days after the campaign finished.';
    default:
      break;
  }

  switch (code) {
    case 'permission-denied':
      return 'Your role cannot manage eDM campaigns. Ask a Super Admin.';
    case 'already-exists':
      return 'This upload was already used for a different file. Start a new import.';
    case 'unauthenticated':
      return 'Your session has expired. Sign in again.';
    case 'deadline-exceeded':
    case 'unavailable':
      return 'This is taking longer than expected. Refresh the page in a minute to see the result.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

// ---------------------------------------------------------------- import

export type EdmFileRole = 'contacts' | 'unsubscribed' | 'bounced';

/** Pre-selects the file role from a vendor export's filename. */
export function suggestRoleFromFilename(name: string): EdmFileRole {
  const n = name.toLowerCase();
  if (n.includes('unsub')) return 'unsubscribed';
  if (/clean|bounce/.test(n)) return 'bounced';
  return 'contacts';
}

/**
 * Import consent attestation, version v1 (context/r6-legal.md §3.3). The
 * Privacy Officer reviews this one string. It is duplicated verbatim on the
 * server, rendered with the tenant's company name, and sent as
 * `consent: { attestationVersion, attestationText }`; the server compares it
 * (trimmed, whitespace collapsed) against its own copy and stores it. Any
 * wording change needs a new version, and the pinned sha256 in
 * utils/__tests__/edm.test.ts must move with it on both sides.
 */
export const EDM_IMPORT_ATTESTATION_VERSION = 'v1';
export const EDM_IMPORT_ATTESTATION_TEMPLATE =
  'I confirm, for {companyName}, that every person in this file agreed to receive marketing email from {companyName}, and that we collected their details ourselves or someone collected them for us, not by buying, renting or scraping a list. I confirm we told them how we would use their details, and that this file includes everyone who unsubscribed from, bounced on, or complained about our email in the system it came from. I understand that VALUTTEN sends these emails for us as our service provider, that we are responsible for having consent, that we must provide proof of consent if asked, and that VALUTTEN may pause or stop our campaigns if complaints or bounces are too high.';

/** The attestation with the company name filled in ("your business" when unknown). */
export function renderImportAttestation(companyName: string | null | undefined): string {
  const name = (companyName ?? '').trim() || 'your business';
  return EDM_IMPORT_ATTESTATION_TEMPLATE.split('{companyName}').join(name);
}

// ---------------------------------------------------------------- design system

/**
 * Tailwind class sets for the eDM pages, copied from the house patterns
 * (ReengagementSettingsPage CARD/INPUT/LABEL, wizard secondary button,
 * LeadCaptureTemplatesPage banners). Classes only; no hex values.
 */
export const EDM_UI = {
  card: 'rounded-xl border border-gray-700 bg-gray-800/40 p-6 space-y-4',
  input:
    'w-full rounded-lg border border-gray-600 bg-gray-900 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none',
  label: 'block text-sm font-medium text-gray-300 mb-1',
  heading: 'flex items-center gap-2 text-base font-semibold text-gray-100',
  headingIcon: 'h-4 w-4 text-blue-400',
  primaryButton:
    'inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50',
  secondaryButton:
    'inline-flex items-center gap-2 rounded-md bg-gray-800 border border-gray-700 hover:bg-gray-700 text-white text-sm px-3 py-1.5 disabled:opacity-50',
  dangerButton:
    'inline-flex items-center gap-2 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm px-3 py-1.5 disabled:opacity-50',
  segmentActive: 'flex-1 rounded-lg border px-4 py-3 text-sm font-medium border-blue-500 bg-blue-500/10 text-blue-200',
  segmentIdle:
    'flex-1 rounded-lg border px-4 py-3 text-sm font-medium border-gray-600 bg-gray-900 text-gray-300 hover:border-gray-500',
  infoBanner: 'rounded-lg border border-blue-500/30 bg-blue-500/10 p-3 text-sm text-blue-200',
  warnBanner: 'rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-300',
  errorBanner: 'rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300',
  chip: 'inline-flex items-center rounded-full px-2 py-0.5 text-xs border',
} as const;

// ---------------------------------------------------------------- time

const BRISBANE_DATETIME = new Intl.DateTimeFormat('en-AU', {
  timeZone: BUSINESS_TZ,
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

const BRISBANE_DATETIME_WEEKDAY = new Intl.DateTimeFormat('en-AU', {
  timeZone: BUSINESS_TZ,
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

/** 'd MMM yyyy, h:mm a' in Brisbane, or '' for a missing or invalid value. */
export function formatBrisbaneDateTime(iso: string | null | undefined, withWeekday = false): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return (withWeekday ? BRISBANE_DATETIME_WEEKDAY : BRISBANE_DATETIME).format(d);
}

/** 'h:mm a' in the browser's own timezone. */
export function formatLocalTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
}

/** True when the browser is not on UTC+10 at that instant. */
export function browserOffsetDiffersFromBrisbane(iso: string): boolean {
  return new Date(iso).getTimezoneOffset() !== -600;
}
